import { useCallback, useState } from 'react';

/**
 * A surface opened from a rendered message row, holding the row itself and not just its
 * id. Virtualization can unmount the source row at any time, and feed-only messages have
 * no legacy `messages` duplicate to re-fetch, so the row travels with the target.
 */
export interface RetainedMessageTarget {
  readonly messageId: string;
  readonly message: Record<string, unknown> | null;
}

/**
 * Every message-launched surface the feed owns. Listing them here rather than in separate
 * useState calls is what makes closeAllTargets and closeTargetsForMessage total: a surface
 * added to this list is covered by both without touching either call site.
 */
export const MESSAGE_TARGET_NAMES = ['editing', 'fullMessage', 'delete', 'conferenceTranscript'] as const;

export type MessageTargetName = (typeof MESSAGE_TARGET_NAMES)[number];

export type MessageTargets = Readonly<Record<MessageTargetName, RetainedMessageTarget | null>>;

const NO_MESSAGE_TARGETS: MessageTargets = {
  editing: null,
  fullMessage: null,
  delete: null,
  conferenceTranscript: null
};

export interface MessageTargetsController {
  readonly targets: MessageTargets;
  /** Opens (or retargets) one surface with the row that launched it. */
  readonly openTarget: (name: MessageTargetName, messageId: unknown, message: Record<string, unknown> | null) => void;
  readonly closeTarget: (name: MessageTargetName) => void;
  /** Closes everything — used when the feed switches channels. */
  readonly closeAllTargets: () => void;
  /** Closes every surface opened from one message, e.g. when that message is deleted. */
  readonly closeTargetsForMessage: (messageId: string) => void;
}

export function useMessageTargets(): MessageTargetsController {
  const [targets, setTargets] = useState<MessageTargets>(NO_MESSAGE_TARGETS);

  const openTarget = useCallback((name: MessageTargetName, messageId: unknown, message: Record<string, unknown> | null) => {
    if (messageId == null) return;
    setTargets((current) => ({ ...current, [name]: { messageId: String(messageId), message } }));
  }, []);

  const closeTarget = useCallback((name: MessageTargetName) => {
    setTargets((current) => (current[name] ? { ...current, [name]: null } : current));
  }, []);

  const closeAllTargets = useCallback(() => {
    setTargets((current) => (MESSAGE_TARGET_NAMES.some((name) => current[name]) ? NO_MESSAGE_TARGETS : current));
  }, []);

  const closeTargetsForMessage = useCallback((messageId: string) => {
    setTargets((current) => {
      const closed = MESSAGE_TARGET_NAMES.filter((name) => current[name]?.messageId === messageId);
      if (closed.length === 0) return current;
      const next = { ...current };
      for (const name of closed) next[name] = null;
      return next;
    });
  }, []);

  return { targets, openTarget, closeTarget, closeAllTargets, closeTargetsForMessage };
}
